// 集中输入校验与错误提示统一模块
// 供计算页(Form)与计算流程(提交前校验)共用

import { PROTECTION_TYPE_SET } from './protectionTypes';

// 字段级规则（按类型触发）
const FIELD_RULES = {
  systemVoltage: {
    types: ['distance', 'directional'],
    required: true,
    range: [0.4, 550],
    messageRequired: '请输入系统电压',
    messageRange: '系统电压应在 0.4~550 kV',
  },
  ratedCurrent: {
    types: ['overcurrent', 'differential', 'zero_sequence', 'longitudinal', 'directional'],
    required: true,
    range: [1, 10000],
    messageRequired: '请输入额定电流',
    messageRange: '额定电流应在 1~10000 A',
  },
  ctRatio: {
    types: ['overcurrent', 'distance', 'differential', 'directional', 'zero_sequence', 'longitudinal'],
    required: true,
    range: [1, 20000],
    messageRequired: '请输入CT变比',
    messageRange: 'CT变比应在 1~20000（一次:二次）',
  },
  ptRatio: {
    types: ['distance', 'directional'],
    required: true,
    range: [1, 10000],
    messageRequired: '请输入PT变比',
    messageRange: 'PT变比应在 1~10000（一次:二次）',
  },
  faultCurrent: {
    types: ['overcurrent', 'distance', 'directional', 'zero_sequence', 'longitudinal'],
    required: true,
    range: [1, 100000],
    messageRequired: '请输入故障电流',
    messageRange: '故障电流应在 1~100000 A',
  },
  transformerCapacity: {
    types: ['differential'],
    required: true,
    range: [100, 1000000],
    messageRequired: '请输入变压器容量',
    messageRange: '变压器容量应在 100~1,000,000 kVA',
  },
  curveType: {
    types: ['overcurrent', 'directional', 'zero_sequence'],
    required: true,
    messageRequired: '请选择曲线族',
  },
  timeMultiplier: {
    types: ['overcurrent', 'directional', 'zero_sequence'],
    required: true,
    range: [0.05, 1.0],
    messageRequired: '请输入时间倍数TMS',
    messageRange: '时间倍数 (TMS) 应在 0.05~1.00 范围',
  },
  // 过电流高级可选输入
  maxLoadCurrent: {
    types: ['overcurrent'],
    required: false,
    range: [1, 100000],
    messageRange: '最大负荷电流应在 1~100000 A',
  },
  pickupMultiplier: {
    types: ['overcurrent'],
    required: false,
    range: [1.0, 5.0],
    messageRange: '启动倍数建议在 1.0~5.0 范围',
  },
  instantaneousMultiplier: {
    types: ['overcurrent'],
    required: false,
    range: [1.0, 30.0],
    messageRange: '瞬时倍数建议在 1.0~30.0 范围',
  },
  polarizingAngle: {
    types: ['directional'],
    required: false,
    range: [0, 90],
    messageRange: '极化角 (°) 应在 0~90 范围',
  },
  k0: {
    types: ['zero_sequence'],
    required: false,
    range: [0, 1],
    messageRange: '零序比例系数 k0 应在 0~1 范围',
  },
  // 距离保护可选线路参数校验
  lineR_ohm: {
    types: ['distance'],
    required: false,
    range: [0, 1000000],
    messageRange: '线路电阻 R(Ω) 应为非负数值',
  },
  lineX_ohm: {
    types: ['distance'],
    required: false,
    range: [0, 1000000],
    messageRange: '线路电抗 X(Ω) 应为非负数值',
  },
  lineAngleDeg: {
    types: ['distance'],
    required: false,
    range: [0, 90],
    messageRange: '线路阻抗角 (°) 应在 0~90 范围',
  },
  // 距离保护新增：长度与单位阻抗（Ω/km）
  lineLength_km: {
    types: ['distance'],
    required: false,
    range: [0, 10000],
    messageRange: '线路长度 (km) 应为非负数值',
  },
  lineR_per_km_ohm: {
    types: ['distance'],
    required: false,
    range: [0, 1000000],
    messageRange: '单位电阻 R(Ω/km) 应为非负数值',
  },
  lineX_per_km_ohm: {
    types: ['distance'],
    required: false,
    range: [0, 1000000],
    messageRange: '单位电抗 X(Ω/km) 应为非负数值',
  },
};

// 生成 AntD Form.Item 的 rules 数组
export const getFieldRules = (protectionType, field) => {
  const rule = FIELD_RULES[field];
  if (!rule || !rule.types.includes(protectionType)) return [];
  const rules = [];
  if (rule.required) {
    rules.push({ required: true, message: rule.messageRequired || '请输入该项' });
  }
  if (rule.range) {
    const [min, max] = rule.range;
    rules.push({
      validator: (_, value) => {
        if (value === undefined || value === null || value === '') return Promise.resolve();
        if (typeof value !== 'number') return Promise.reject(rule.messageRange || '输入值需为数字');
        if (value < min || value > max) return Promise.reject(rule.messageRange || `输入值需在 ${min}~${max} 范围`);
        return Promise.resolve();
      }
    });
  }
  return rules;
};

// 提供范围提示（页面可用于 extra 展示）
export const getFieldRange = (protectionType, field) => {
  const rule = FIELD_RULES[field];
  if (!rule) return null;
  if (!rule.types || !rule.types.includes(protectionType)) return null;
  if (!Array.isArray(rule.range)) return null;
  return rule.range;
};

// 判断某字段是否适用于指定保护类型（用于 UI 显示过滤）
export const isFieldApplicable = (protectionType, field) => {
  const rule = FIELD_RULES[field];
  if (!rule) {
    // 未在规则表中的字段，默认认为可显示（如 protectionType、本类型专有展示项）
    return true;
    }
  return Array.isArray(rule.types) && rule.types.includes(protectionType);
};

// 软校核提示阈值（不阻断，仅警告）
const SOFT_WARN_LIMITS = {
  timeMultiplierHigh: 0.9,
  ctRatioHigh: 5000,
  faultCurrentHigh: 50000,
  polarizingAngleTypical: [20, 60],
  k0Typical: [0.2, 0.5],
};

// 提交前统一校验（返回统一错误与警告提示）
export const validateInputs = (values) => {
  const errors = [];
  const warnings = [];
  let valid = true;

  const type = values?.protectionType;
  if (!type) {
    errors.push('请选择保护类型');
    return { valid: false, errors, warnings };
  }
  if (!PROTECTION_TYPE_SET.has(type)) {
    errors.push('保护类型不在支持范围');
    return { valid: false, errors, warnings };
  }

  // 针对该类型的字段遍历校验
  Object.entries(FIELD_RULES).forEach(([field, rule]) => {
    if (!rule.types.includes(type)) return;
    const value = values?.[field];

    if (rule.required && (value === undefined || value === null || value === '')) {
      valid = false;
      if (rule.messageRequired) errors.push(rule.messageRequired);
      return;
    }

    // 范围校验
    if (rule.range && value !== undefined && value !== null && value !== '') {
      const [min, max] = rule.range;
      if (typeof value !== 'number' || value < min || value > max) {
        valid = false;
        if (rule.messageRange) errors.push(rule.messageRange);
      }
    }
  });

  // 软校核警告（不阻断计算）
  if (type && ['overcurrent', 'directional', 'zero_sequence'].includes(type)) {
    const tms = values?.timeMultiplier;
    if (typeof tms === 'number' && tms > SOFT_WARN_LIMITS.timeMultiplierHigh) {
      warnings.push('时间倍数 TMS 接近上限，可能影响选择性');
    }
  }
  if (typeof values?.ctRatio === 'number' && values.ctRatio > SOFT_WARN_LIMITS.ctRatioHigh) {
    warnings.push('CT 变比较大，注意CT饱和与误差对保护的影响');
  }
  if (typeof values?.faultCurrent === 'number' && values.faultCurrent > SOFT_WARN_LIMITS.faultCurrentHigh) {
    warnings.push('故障电流数值很大，请确认一次参数与短路计算');
  }
  if (type === 'directional' && typeof values?.polarizingAngle === 'number') {
    const [amin, amax] = SOFT_WARN_LIMITS.polarizingAngleTypical;
    if (values.polarizingAngle < amin || values.polarizingAngle > amax) {
      warnings.push('极化角超出典型范围（20°~60°），请结合潮流校核');
    }
  }
  if (type === 'zero_sequence' && typeof values?.k0 === 'number') {
    const [kmin, kmax] = SOFT_WARN_LIMITS.k0Typical;
    if (values.k0 < kmin || values.k0 > kmax) {
      warnings.push('k0 超出典型范围（0.2~0.5），请结合接地方式校核');
    }
  }

  return { valid, errors, warnings };
};